/**
 * FileList — Staged files list molecule.
 *
 * Shows the list of files staged for transfer in BitDrop.
 * Header: "+ Add more" tactile button & formatted summary (e.g. "2 files · 1.4 GB").
 * Rows: FileTypeIcon, truncated filename, formatted size badge, and remove (x) button.
 */
import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ScrollView } from 'react-native';
import { useTheme } from '../hooks/useTheme';
import { Icon } from '../atoms/Icon';
import { FileTypeIcon } from './FileTypeIcon';
import { Spacing, FontSize, FontWeight, Radius } from '../theme/tokens';

// ─── Size Formatter ───────────────────────────────────────────────────────────

export const fmtSize = (bytes: number): string => {
  if (!bytes || bytes <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / Math.pow(1024, i);
  return `${value.toFixed(value >= 100 || i === 0 ? 0 : 1)} ${units[i]}`;
};

// ─── Props ───────────────────────────────────────────────────────────────────

export interface FileListProps {
  files: File[];
  /** Opens the file picker to ADD more files to staging. */
  onAddMoreFiles: () => void;
  onRemoveFile: (index: number) => void;
}

// ─── Component ───────────────────────────────────────────────────────────────

export const FileList: React.FC<FileListProps> = ({ files, onAddMoreFiles, onRemoveFile }) => {
  const { colors } = useTheme();

  const totalBytes = files.reduce((acc, f) => acc + (f.size || 0), 0);
  const totalCount = files.length;
  const countLabel = `${totalCount} ${totalCount === 1 ? 'file' : 'files'}`;
  const totalSummary = `${countLabel} · ${fmtSize(totalBytes)}`;

  return (
    <View style={styles.container}>
      {/* ── Header: Add More Button + Total Summary Badge ── */}
      <View style={styles.header}>
        <TouchableOpacity
          onPress={onAddMoreFiles}
          style={[styles.addMoreBtn, { backgroundColor: colors.pill, borderColor: colors.border }]}
          activeOpacity={0.75}
        >
          <View style={[styles.addDot, { backgroundColor: colors.text }]}>
            <Icon name="plus" size={10} color={colors.surface as string} strokeWidth={3} />
          </View>
          <Text style={[styles.addMoreLabel, { color: colors.text }]}>Add more</Text>
        </TouchableOpacity>

        <Text style={[styles.summaryText, { color: colors.subtext }]}>
          {totalSummary}
        </Text>
      </View>

      {/* ── Scrollable Staged Files List ── */}
      <ScrollView
        style={styles.list}
        contentContainerStyle={styles.listContent}
        nestedScrollEnabled
        showsVerticalScrollIndicator={false}
      >
        {files.map((file, idx) => (
          <View
            key={`${file.name}-${idx}`}
            style={[styles.row, { borderBottomColor: colors.border }]}
          >
            {/* File type icon */}
            <View style={[styles.iconWrap, { backgroundColor: colors.pill }]}>
              <FileTypeIcon filename={file.name} size={14} color={colors.text as string} />
            </View>

            {/* Filename */}
            <Text
              style={[styles.filename, { color: colors.text }]}
              numberOfLines={1}
              ellipsizeMode="middle"
            >
              {file.name}
            </Text>

            {/* Size */}
            <Text style={[styles.sizeBadge, { color: colors.subtext }]}>
              {fmtSize(file.size)}
            </Text>

            {/* Remove Action */}
            <TouchableOpacity
              onPress={() => onRemoveFile(idx)}
              style={styles.removeBtn}
              hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
              activeOpacity={0.6}
              accessibilityLabel={`Remove ${file.name}`}
            >
              <Icon name="close" size={14} color={colors.subtext as string} strokeWidth={2} />
            </TouchableOpacity>
          </View>
        ))}
      </ScrollView>
    </View>
  );
};

// ─── Styles ───────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    width: '100%',
    marginBottom: Spacing.sm,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: Spacing.sm,
    paddingHorizontal: 2,
  },
  addMoreBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingVertical: 5,
    paddingHorizontal: 10,
    borderRadius: Radius.pill,
    borderWidth: 1,
  } as any,
  addDot: {
    width: 16,
    height: 16,
    borderRadius: 8,
    justifyContent: 'center',
    alignItems: 'center',
  },
  addMoreLabel: {
    fontSize: FontSize.xs,
    fontWeight: FontWeight.semibold,
    letterSpacing: -0.1,
  },
  summaryText: {
    fontSize: FontSize.xs,
    fontWeight: FontWeight.medium,
    fontVariant: ['tabular-nums'] as any,
    letterSpacing: -0.1,
  },
  list: {
    maxHeight: 140,
    width: '100%',
  },
  listContent: {
    paddingVertical: 2,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
    borderBottomWidth: 1,
    gap: 10,
  } as any,
  iconWrap: {
    width: 28,
    height: 28,
    borderRadius: Radius.sm,
    alignItems: 'center',
    justifyContent: 'center',
  },
  filename: {
    flex: 1,
    fontSize: FontSize.xs + 1,
    fontWeight: FontWeight.semibold,
    letterSpacing: -0.1,
  },
  sizeBadge: {
    fontSize: FontSize.xs,
    fontWeight: FontWeight.medium,
    fontVariant: ['tabular-nums'] as any,
    marginRight: 2,
  },
  removeBtn: {
    padding: 4,
    borderRadius: Radius.full,
    alignItems: 'center',
    justifyContent: 'center',
  },
});
